/**
 * A waveform-shaped envelope, from a seed.
 *
 * Audio has no thumbnail, so the product draws one. This is honestly *not* the
 * decoded audio: computing a real waveform means downloading and decoding the
 * whole file before anything can be shown, which is a lot of bytes and a lot
 * of latency for a motif. What it is instead is stable: the same recording
 * always draws the same shape, so a voice note looks like itself in the
 * library, on the share page, and in a list of six others.
 *
 * Bars are returned as heights in 0..1.
 */

/** A stable 32-bit hash (FNV-1a). Not cryptographic; it only has to spread. */
function hash(value: string): number {
  let result = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    result ^= value.charCodeAt(index);
    result = Math.imul(result, 16777619);
  }
  return result >>> 0;
}

export function waveformEnvelope(seed: string, count: number): number[] {
  const base = hash(seed);
  // Two detuned frequencies plus a phase offset: enough variety that two
  // recordings rarely look alike, little enough that none look like noise.
  const fast = 12 + (base % 17);
  const slow = 3 + ((base >>> 8) % 11);
  const phase = ((base >>> 16) % 628) / 100;

  return Array.from({ length: count }, (_, index) => {
    const t = index / Math.max(1, count - 1);
    const shape = 0.55 + 0.45 * Math.sin(t * fast + phase) * Math.cos(t * slow);
    // Taper the ends so it reads as a clip with a start and a finish.
    const taper = Math.sin(Math.PI * t) ** 0.45;
    return Math.max(0.16, Math.min(1, shape * taper));
  });
}
